import { Component, Inject, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BehaviorSubject, ReplaySubject, Subject, switchMap } from 'rxjs';
import { CalculateCoverResponse } from 'src/app/models/calculate-cover-response';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { QuoteModel } from 'src/app/models/quote.model';
import { ApiService } from 'src/app/services/api.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { proccessCookie } from 'src/app/util/cookie-handler';
import { Loader } from 'src/app/util/loader';
import { parseQuery } from 'src/app/util/parse-query';
import { SITE_CONTENT_CLICK, SiteContentClickEvent } from '../shared/site-content/site-content.component';
import { AnalyticsService } from 'src/app/services/analytics.service';

@Component({
  selector: 'app-apg-quick-quote',
  templateUrl: './apg-quick-quote.component.html',
  styleUrls: ['./apg-quick-quote.component.scss'],
  providers: [
    { provide: SITE_CONTENT_CLICK, useFactory: () => new Subject<SiteContentClickEvent>() },
    { provide: Loader, useValue: new Loader() }
  ]
})
export class ApgQuickQuoteComponent {
  @Input() name: string;
  @ViewChild(NgForm) form: NgForm;

  quickquote$ = new BehaviorSubject<QQQuestionnaireViewModel>(null);
  refresh$ = new ReplaySubject<boolean>(1);
  mode: 'quickquote' | 'coverCalculator' | 'fullquote' = 'quickquote';
  error: string;
  quoteKey: string;
  quote: QuoteModel;
  selectedQuote: QuoteModel;

  constructor(
    private questionnaireService: QuestionnaireService,
    private api: ApiService,
    private analyticsService: AnalyticsService,
    @Inject(SITE_CONTENT_CLICK) private siteContentClick: Subject<SiteContentClickEvent>,
    public loader: Loader
  ) {
  }

  ngOnInit() {
    this.siteContentClick.subscribe(x => {
      const target = x.event.currentTarget as HTMLAnchorElement;
      if (target.href === 'https://app/cover-calculator') {
        // we need a delay before changing mode so that the help popup can close
        setTimeout(() => this.mode = 'coverCalculator', 500);
        x.event.preventDefault();
      }
    });
  }

  ngAfterViewInit() {
    this.refresh$.pipe(
      switchMap(x => this.loadViewModel())
    ).subscribe(this.quickquote$);

    this.refresh$.next(true);
  }

  async loadViewModel() {
    try {
      this.loader.loading = true;
      const model = await this.questionnaireService.getQuestionnaireViewModel(this.name, true);
      model.sourceUrl = this.analyticsService.getFullPath('quick_quote');   

      const query = parseQuery(location.search);
      if (query['ref']) {
        this.quoteKey = query['ref'];
        this.mode = 'fullquote';
      } else if (query['quoteKey']) {
        this.quote = await this.api.getQuote(query['quoteKey'], query['dob'], query['lastName']);
        for (const ans of this.quote.answers) {
          model.quoteKey = this.quote.key;
          model.saveAnswer(ans.key, ans.value);
        }
        model.goToPage(parseInt(query['page']));
      } else {
        model.saveAnswer('CoverBasis_1', query['CoverBasis'] || null);
        model.saveAnswer('ProtectionLevel_1', query['SumAssured'] || null);
        model.saveAnswer('PolicyTerm_1', query['Term'] || null);
        if (query['SumAssured']) {
          model.saveAnswer('CoverSource', 'CoverCalculator')
          model.saveAnswer('FinancialAllocationType', 'SumAssured');
          model.saveAnswer('PolicyType_1', 'TERM')
        }
      }

      this.analyticsService
      .mapTrackingData()?.map(x => model.saveAnswer(x.key, x.value)) ?? [];

      return model;
    } catch (e) {
      console.log(e);
      this.error = `Sorry, there was an error loading the ${this.name} questionnaire`;
    } finally {
      this.loader.loading = false;
    }
  }

  onCoverCalculated(result: CalculateCoverResponse) {
    const quote = this.quickquote$.value;
    quote.saveAnswer('CoverBasis_1', result.coverBasis);
    quote.saveAnswer('ProtectionLevel_1', result.sumAssured.toFixed(0));
    quote.saveAnswer('PolicyTerm_1', result.term.toFixed(0));
    quote.saveAnswer('FinancialAllocationType', 'SumAssured')
    quote.saveAnswer('PolicyType_1', 'TERM')
    quote.saveAnswer('CoverSource', 'CoverCalculator')
    quote.currentPage.nextPage();

    this.mode = 'quickquote';
  }

  open(link: string) {
    if (link == 'CoverCalculator')
      this.mode = 'coverCalculator';
    else
      window.location.href = link;
  }

  goToFullQuote() {
    this.mode = 'fullquote';
  }

  async saveOtp(qq: QQQuestionnaireViewModel, code: string) {
    qq.saveAnswer('OtpCode', code);
    await qq.currentPage.nextPage();
  }

  // updateQuickQuote(qq: QQQuestionnaireViewModel) {
  //   const quote = this.quickquote$.value;
  //   quote.saveAnswer('FirstPolicyHolderEmail', qq.answersObject.FirstPolicyHolderEmail);
  //   quote.saveAnswer('FirstPolicyHolderMobile', qq.answersObject.FirstPolicyHolderMobile);
  // }

  saveCoverBasis(result: QuoteModel) {
    var coverbasis = result.answers.find(x => x.key === 'CoverBasis_1')?.value;
    const quote = this.quickquote$.value;
    quote.saveAnswer('CoverBasis_1', coverbasis);

    if (result.result?.sumAssured) {
      quote.saveAnswer('ProtectionLevel_1', result.result.sumAssured.toString());
    }

    this.quote = result;
    this.quoteKey = result.key;
    this.quickquote$.value.currentPage.nextPage();
  }

  quoteSelected(result: QuoteModel) {
    try {
      this.selectedQuote = result;
      this.quoteKey = result.key;

      var hasCi = result.answers.find(x => x.key === 'PolicyType_2')?.value.endsWith('-CI');
      if (hasCi) {
        for (let ans of result.answers.filter((x) => x.key.endsWith('_2')))
          this.quickquote$.value.saveAnswer(ans.key, ans.value);
      }
      else {
        for (const key in this.quickquote$.value.answersObject) {
          if (key.endsWith('_2')) {
            delete this.quickquote$.value.answersObject[key];
          }
        }
        this.quickquote$.value.answers = new Map(
          Array.from(this.quickquote$.value.answers).filter(([key, value]) => !key.endsWith('_2'))
        );
      }
      // const quote = this.quickquote$.value;
      // const policytype = result.answers.find(x => x.key === 'PolicyType_1')?.value;
      // quote.quoteKey = result.key;
      // quote.saveAnswer('PolicyType_1', policytype);

      // if (result.result?.sumAssured) {
      //   quote.saveAnswer('ProtectionLevel_1', result.result.sumAssured.toString());
      // }
      this.quickquote$.value.currentPage.nextPage();
    } catch (e) {
      console.log(e);
      this.error = `Sorry, there was an error loading the ${this.name} questionnaire`;
    }
  }

  continueQuote() {
    this.mode = 'fullquote';
  }

  backToQuickQuote() {
    //remove any CI selected values
    const q = this.quickquote$.value;
    q.sourceUrl = this.analyticsService.getFullPath('quick_quote');
    this.quickquote$.value.answers = new Map(
      Array.from(q.answers).filter(([key, value]) => !key.endsWith('_2'))
    );
    this.mode = 'quickquote';
  }

  newQuote() {
    this.mode = 'quickquote';
    this.quoteKey = null;
    const q = this.quickquote$.value;
    q.sourceUrl = this.analyticsService.getFullPath('quick_quote');
    q.saveAnswer('CoverBasis_1', null);
    q.saveAnswer('PolicyType_1', null);
    q.saveAnswer('ProtectionLevel_1', null);
    q.saveAnswer('PolicyTerm_1', null);
    q.saveAnswer('CoverSource', null);
    //q.goToPage(5); //this sets up a quote with 0 Sum Assured etc
    q.goToPage(0);
  }
}
